import { supabase } from '../config/supabase';
import { formatCurrency } from '../utils/currency';

export interface InventoryItem {
  id: string;
  business_id: string;
  name: string;
  description?: string;
  category: 'spare_parts' | 'accessories' | 'devices' | 'other';
  sku?: string;
  quantity: number;
  average_cost: number; // Calculated from purchase transactions (read-only)
  purchase_cost: number; // Latest purchase price (read-only)
  sale_price: number; // Selling price (read-only)
  reorder_point: number;
  location?: string;
  supplier_id?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InventoryFormData {
  name: string;
  description?: string;
  category: 'spare_parts' | 'accessories' | 'devices';
  sku: string;
  reorder_point: number;
}

export const inventoryService = {
  // Get all inventory items for the business
  async getInventoryItems(businessId?: string): Promise<{ items: InventoryItem[]; error: Error | null }> {
    try {
      let query = supabase
        .from('inventory_items')
        .select('*')
        .order('name', { ascending: true });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { items: data || [], error: null };
    } catch (error) {
      return { items: [], error: error as Error };
    }
  },

  // Get inventory item by ID
  async getInventoryItemById(id: string, businessId?: string): Promise<{ item: InventoryItem | null; error: Error | null }> {
    try {
      let query = supabase
        .from('inventory_items')
        .select('*')
        .eq('id', id);

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query.single();

      if (error) throw error;
      return { item: data, error: null };
    } catch (error) {
      return { item: null, error: error as Error };
    }
  },

  // Create a new inventory item
  async createInventoryItem(itemData: InventoryFormData): Promise<{ item: InventoryItem | null; error: Error | null }> {
    try {
      // Get current user's business_id
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('No authenticated user');

      const { data: userData, error: userFetchError } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user.id)
        .single();

      if (userFetchError) throw userFetchError;
      if (!userData?.business_id) throw new Error('Business not found');

      const insertData = {
        business_id: userData.business_id,
        name: itemData.name,
        description: itemData.description,
        category: itemData.category,
        sku: itemData.sku,
        quantity: 0,
        average_cost: 0,
        purchase_cost: 0,
        sale_price: 0,
        reorder_point: itemData.reorder_point,
        is_active: true,
      };

      console.log('Inserting inventory item:', insertData);

      const { data, error } = await supabase
        .from('inventory_items')
        .insert([insertData])
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      console.log('Insert successful:', data);
      return { item: data, error: null };
    } catch (error) {
      console.error('Create inventory item error:', error);
      return { item: null, error: error as Error };
    }
  },

  // Update an existing inventory item
  async updateInventoryItem(id: string, itemData: InventoryFormData): Promise<{ item: InventoryItem | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('inventory_items')
        .update({
          name: itemData.name,
          description: itemData.description,
          category: itemData.category,
          sku: itemData.sku,
          reorder_point: itemData.reorder_point,
        })
        .eq('id', id)
        .single();

      if (error) throw error;
      return { item: data, error: null };
    } catch (error) {
      return { item: null, error: error as Error };
    }
  },

  // Delete an inventory item
  async deleteInventoryItem(id: string): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase
        .from('inventory_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Update inventory quantity (for stock adjustments)
  async updateInventoryQuantity(id: string, newQuantity: number): Promise<{ item: InventoryItem | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('inventory_items')
        .update({ quantity: newQuantity })
        .eq('id', id)
        .single();

      if (error) throw error;
      return { item: data, error: null };
    } catch (error) {
      return { item: null, error: error as Error };
    }
  },

  // Update inventory from purchase (automatically updates costs)
  async updateInventoryFromPurchase(
    itemId: string,
    quantity: number,
    unitCost: number,
    referenceType: 'purchase_order' | 'inventory_return',
    referenceId: string
  ): Promise<{ item: InventoryItem | null; error: Error | null }> {
    try {
      // Get current item data
      const { item: currentItem, error: fetchError } = await this.getInventoryItemById(itemId);

      if (fetchError) throw fetchError;
      if (!currentItem) throw new Error('Item not found');

      // Calculate new average cost (weighted average)
      const newAverageCost = this.calculateAverageCost(
        currentItem.quantity,
        currentItem.average_cost,
        quantity,
        unitCost
      );

      // Update item with new quantity, costs, and purchase cost
      const newQuantity = currentItem.quantity + quantity;

      // Prepare update data
      const updateData: any = {
        quantity: newQuantity,
        average_cost: newAverageCost,
        purchase_cost: unitCost, // Update latest purchase cost
      };

      // NOTE: Sale price should NOT be automatically updated when purchasing
      // Sale price is a separate business decision and should be set manually
      // Only update average_cost and purchase_cost for inventory valuation

      const { data, error } = await supabase
        .from('inventory_items')
        .update(updateData)
        .eq('id', itemId)
        .single();

      if (error) throw error;

      // Create inventory transaction record
      await this.createInventoryTransaction({
        item_id: itemId,
        type: referenceType === 'purchase_order' ? 'purchase' : 'return',
        quantity: quantity,
        unit_cost: unitCost,
        reference_type: referenceType,
        reference_id: referenceId,
        business_id: currentItem.business_id, // Use the item's business_id
      });

      return { item: data, error: null };
    } catch (error) {
      return { item: null, error: error as Error };
    }
  },

  // Update inventory from usage (repair orders, sales)
  async updateInventoryFromUsage(
    itemId: string,
    quantity: number,
    referenceType: 'repair_order' | 'invoice',
    referenceId: string
  ): Promise<{ item: InventoryItem | null; error: Error | null }> {
    try {
      // Get current item data
      const { item: currentItem, error: fetchError } = await this.getInventoryItemById(itemId);

      if (fetchError) throw fetchError;
      if (!currentItem) throw new Error('Item not found');

      // Check if enough quantity available
      if (currentItem.quantity < quantity) {
        throw new Error('Not enough inventory available');
      }

      // Update item with new quantity
      const newQuantity = currentItem.quantity - quantity;

      const { data, error } = await supabase
        .from('inventory_items')
        .update({
          quantity: newQuantity,
        })
        .eq('id', itemId)
        .single();

      if (error) throw error;

      // Create inventory transaction record
      await this.createInventoryTransaction({
        item_id: itemId,
        type: referenceType === 'repair_order' ? 'repair_usage' : 'sale',
        quantity: -quantity, // Negative for usage
        unit_cost: currentItem.average_cost,
        reference_type: referenceType,
        reference_id: referenceId,
        business_id: currentItem.business_id, // Use the item's business_id
      });

      return { item: data, error: null };
    } catch (error) {
      return { item: null, error: error as Error };
    }
  },

  // Get low stock items
  async getLowStockItems(businessId?: string): Promise<{ items: InventoryItem[]; error: Error | null }> {
    try {
      let query = supabase
        .from('inventory_items')
        .select('*')
        .filter('quantity', 'lte', 'reorder_point')
        .eq('is_active', true)
        .order('quantity', { ascending: true });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { items: data || [], error: null };
    } catch (error) {
      return { items: [], error: error as Error };
    }
  },

  // Calculate average cost (weighted average) - optimized for Indonesian Rupiah
  calculateAverageCost(
    existingQuantity: number,
    existingCost: number,
    newQuantity: number,
    newCost: number
  ): number {
    if (existingQuantity + newQuantity === 0) {
      return 0;
    }

    // Handle large Indonesian amounts (millions/billions) with high precision
    const precision = 4; // Use 4 decimal places for Rupiah calculations
    const multiplier = Math.pow(10, precision);

    // Round to prevent floating-point precision errors
    const existingQty = Math.round(existingQuantity * 1000) / 1000; // 3 decimal places for quantities
    const existingCostPrecise = Math.round(existingCost * multiplier) / multiplier;
    const newQty = Math.round(newQuantity * 1000) / 1000;
    const newCostPrecise = Math.round(newCost * multiplier) / multiplier;

    // Calculate with high precision to handle large Rupiah amounts
    const totalCost = (existingQty * existingCostPrecise) + (newQty * newCostPrecise);
    const totalQuantity = existingQty + newQty;

    if (totalQuantity === 0) {
      return 0;
    }

    const averageCost = totalCost / totalQuantity;

    // Round final result to 2 decimal places for Rupiah (though typically whole numbers)
    const finalAverageCost = Math.round(averageCost * 100) / 100;

    // Debug logging for Indonesian Rupiah calculations
    console.log('🇮🇩 Indonesian Rupiah Average Cost Calculation:', {
      existingQuantity: existingQty,
      existingCost: existingCostPrecise.toLocaleString('id-ID'),
      newQuantity: newQty,
      newCost: newCostPrecise.toLocaleString('id-ID'),
      totalCost: totalCost.toLocaleString('id-ID'),
      totalQuantity,
      averageCost: finalAverageCost.toLocaleString('id-ID'),
      formatted: new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        minimumFractionDigits: 0,
      }).format(finalAverageCost)
    });

    return finalAverageCost;
  },

  // Create inventory transaction record
  async createInventoryTransaction(transactionData: {
    item_id: string;
    type: 'purchase' | 'repair_usage' | 'return' | 'sale';
    quantity: number;
    unit_cost: number;
    reference_type?: string;
    reference_id?: string;
    business_id?: string; // Add business_id parameter
  }): Promise<{ error: Error | null }> {
    try {
      // Use provided business_id or get from current user
      let businessId = transactionData.business_id;

      if (!businessId) {
        // Get current user's business_id as fallback
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        if (!user) throw new Error('No authenticated user');

        const { data: userData, error: userFetchError } = await supabase
          .from('users')
          .select('business_id')
          .eq('id', user.id)
          .single();

        if (userFetchError) throw userFetchError;
        businessId = userData.business_id;
      }

      const { error } = await supabase
        .from('inventory_transactions')
        .insert([
          {
            business_id: businessId,
            item_id: transactionData.item_id,
            type: transactionData.type,
            quantity: transactionData.quantity,
            unit_cost: transactionData.unit_cost,
            reference_type: transactionData.reference_type,
            reference_id: transactionData.reference_id,
          },
        ]);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  },

  // Get inventory transactions for an item
  async getInventoryTransactions(itemId: string, businessId?: string): Promise<{ transactions: any[]; error: Error | null }> {
    try {
      // First get the transactions
      let query = supabase
        .from('inventory_transactions')
        .select('*')
        .eq('item_id', itemId)
        .order('created_at', { ascending: false });

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data: transactions, error } = await query;
      if (error) throw error;

      if (!transactions || transactions.length === 0) {
        return { transactions: [], error: null };
      }

      // Get purchase order numbers for transactions that reference purchase orders
      const poTransactions = transactions.filter(t => t.reference_type === 'purchase_order');
      const poIds = poTransactions.map(t => t.reference_id);

      let poNumbers: { [key: string]: string } = {};

      if (poIds.length > 0) {
        const { data: purchaseOrders, error: poError } = await supabase
          .from('purchase_orders')
          .select('id, po_number')
          .in('id', poIds);

        if (!poError && purchaseOrders) {
          poNumbers = purchaseOrders.reduce((acc, po) => {
            acc[po.id] = po.po_number;
            return acc;
          }, {} as { [key: string]: string });
        }
      }

      // Transform transactions to include purchase order numbers
      const transformedTransactions = transactions.map(transaction => ({
        ...transaction,
        reference_number: transaction.reference_type === 'purchase_order'
          ? (poNumbers[transaction.reference_id] || transaction.reference_id)
          : transaction.reference_id
      }));

      return { transactions: transformedTransactions, error: null };
    } catch (error) {
      return { transactions: [], error: error as Error };
    }
  },

  // Get formatted cost
  getFormattedCost(cost: number): string {
    return formatCurrency(cost);
  },

  // Generate SKU based on category
  generateSKU(category: string): string {
    const prefixes: Record<string, string> = {
      spare_parts: 'SP',
      accessories: 'AC',
      devices: 'DV',
    };

    const prefix = prefixes[category] || 'IT';
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');

    return `${prefix}-${timestamp}${random}`;
  },

  // Recalculate average cost from all inventory transactions
  async recalculateAverageCostFromTransactions(itemId: string, businessId?: string): Promise<{ item: InventoryItem | null; error: Error | null }> {
    try {
      // Get all inventory transactions for this item (excluding the ones being deleted)
      const { transactions, error: transactionsError } = await this.getInventoryTransactions(itemId, businessId);

      if (transactionsError) throw transactionsError;

      console.log(`Recalculating average cost for item ${itemId}, business ${businessId}`);
      console.log(`Found ${transactions.length} total transactions:`, transactions);

      // Filter out negative transactions (usage/sales) and calculate weighted average
      const purchaseTransactions = transactions.filter(t => t.quantity > 0);
      console.log(`Found ${purchaseTransactions.length} purchase transactions:`, purchaseTransactions);

      if (purchaseTransactions.length === 0) {
        // No purchase transactions left, set average cost to 0
        const { data, error } = await supabase
          .from('inventory_items')
          .update({
            average_cost: 0,
            purchase_cost: 0
          })
          .eq('id', itemId)
          .eq('business_id', businessId || '')
          .single();

        if (error) throw error;
        return { item: data, error: null };
      }

      // Calculate weighted average cost from remaining transactions
      let totalCost = 0;
      let totalQuantity = 0;
      let latestPurchaseCost = 0;
      let latestTransactionDate = '';

      for (const transaction of purchaseTransactions) {
        totalCost += transaction.quantity * transaction.unit_cost;
        totalQuantity += transaction.quantity;
        // Keep track of the most recent purchase cost
        if (!latestTransactionDate || transaction.created_at > latestTransactionDate) {
          latestTransactionDate = transaction.created_at;
          latestPurchaseCost = transaction.unit_cost;
        }
      }

      const newAverageCost = totalQuantity > 0 ? totalCost / totalQuantity : 0;

      console.log(`Calculation results: totalCost=${totalCost}, totalQuantity=${totalQuantity}, newAverageCost=${newAverageCost}, latestPurchaseCost=${latestPurchaseCost}`);

      // Update the item with recalculated costs
      const { data, error } = await supabase
        .from('inventory_items')
        .update({
          average_cost: newAverageCost,
          purchase_cost: latestPurchaseCost
        })
        .eq('id', itemId)
        .eq('business_id', businessId || '')
        .single();

      if (error) throw error;
      console.log(`Updated item ${itemId} with average_cost=${newAverageCost}, purchase_cost=${latestPurchaseCost}`);
      return { item: data, error: null };
    } catch (error) {
      return { item: null, error: error as Error };
    }
  },

  // Get category label
  getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
      spare_parts: 'Spare Parts',
      accessories: 'Accessories',
      devices: 'Device',
    };
    return labels[category] || category;
  },
};